import { useState } from "react";
import { postJson } from "../api";
import { RowsSkeleton } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { formatText, useTexts } from "../texts";
import { Notice, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { attemptText, whenIn } from "./organizations";
import { ConfirmWindow } from "./PersonActions";
import { deviceName } from "./security";
import { SimpleTable } from "./SimpleTable";
import styles from "./orgs.module.css";

// Bloques de seguridad de una persona (E48): Estado de acceso, Sesiones abiertas e Intentos de ingreso. Los usan la
// ficha del usuario (pestañas Seguridad y Actividad) y Seguridad y acceso › Recuperar acceso: una sola pieza, no copias.

export type OpenSession = {
  sessionId: string;
  device: string | null;
  browser: string | null;
  os: string | null;
  ip: string | null;
  lastActivityAt: string;
};

export type SecurityState = {
  locked: boolean;
  lockReason: string | null;
  lockedAt: string | null;
  lockedUntil: string | null;
  failedLoginCount: number;
  lastFailedLoginAt: string | null;
  lastLoginAt: string | null;
};

/** Pares etiqueta / valor (C41) en cuatro columnas (dos en el celular). */
export function Facts({ items }: { items: { label: string; value: string }[] }) {
  return (
    <dl className={ui.facts} data-part="datos">
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Estado de acceso: bloqueo (con su motivo y desde cuándo), intentos fallidos y último acceso. Con `account` (Recuperar
 * acceso) el primer dato es la cuenta; sin ella (la ficha), el último fallo.
 */
export function AccessFacts({ security, zone, account }: { security: SecurityState; zone: string; account?: string }) {
  const { L, locale } = useTexts();
  const reason = security.lockReason === "FAILED_LOGIN_ATTEMPTS" ? L("ADM_PPL_LOCK_ATTEMPTS") : L("ADM_PPL_LOCK_ADMIN");
  const lock = !security.locked
    ? L("ADM_PPL_NO_LOCK")
    : security.lockedAt
      ? formatText(L("ADM_SEC_LOCK_SINCE"), reason, whenIn(L, locale, security.lockedAt, zone, true))
      : reason;
  const last = {
    label: L("ADM_PPL_LAST_ACCESS"),
    value: security.lastLoginAt ? whenIn(L, locale, security.lastLoginAt, zone) : L("ADM_PPL_NEVER"),
  };
  const failed = { label: L("ADM_PPL_FAILED_ATTEMPTS"), value: security.failedLoginCount.toLocaleString(locale) };
  return (
    <Facts
      items={
        account !== undefined
          ? [{ label: L("ADM_PPL_ACCOUNT"), value: account }, { label: L("ADM_PPL_LOCK"), value: lock }, failed, last]
          : [
              { label: L("ADM_PPL_LOCK"), value: lock },
              failed,
              {
                label: L("ADM_PPL_LAST_FAILURE"),
                value: security.lastFailedLoginAt ? whenIn(L, locale, security.lastFailedLoginAt, zone) : "—",
              },
              last,
            ]
      }
    />
  );
}

/**
 * Sesiones abiertas de una persona: Dispositivo · IP · Última actividad · «Cerrar», que confirma (`cerrar-sesion`, C12,
 * D8). Sin sesiones, el vacío lo dice; si Identity no respondió, el aviso (nunca vacío, H-3).
 */
export function SessionsBlock({
  orgId,
  userId,
  name,
  sessions,
  zone,
  onChanged,
}: {
  orgId: number;
  userId: number;
  name: string;
  sessions: OpenSession[] | null;
  zone: string;
  onChanged: () => void;
}) {
  const { L, locale } = useTexts();
  const [closing, setClosing] = useState<OpenSession | null>(null);
  if (!sessions) return <Notice tone="warning">{L("ADM_PPL_SESSIONS_UNAVAILABLE")}</Notice>;
  return (
    <>
      {sessions.length === 0 ? (
        <div className="tm-empty" role="status">
          <strong>{L("ADM_PPL_NO_SESSIONS")}</strong>
        </div>
      ) : (
        <SimpleTable
          label={L("ADM_PPL_SESSIONS")}
          rows={sessions}
          rowKey={(session) => session.sessionId}
          columns={[
            {
              key: "device",
              title: L("ADM_PPL_COL_DEVICE"),
              render: (session) => (
                <span className={styles.personText}>
                  <strong>{deviceName(L, session)}</strong>
                  {session.os && session.os !== "UNKNOWN" ? <span>{session.os}</span> : null}
                </span>
              ),
            },
            { key: "ip", title: L("ADM_PPL_COL_IP"), render: (session) => session.ip ?? "—" },
            {
              key: "last",
              title: L("ADM_PPL_COL_ACTIVITY"),
              render: (session) => whenIn(L, locale, session.lastActivityAt, zone),
            },
            {
              key: "close",
              title: "",
              className: styles.endCell,
              render: (session) => (
                <button type="button" className="tm-btn secondary small" onClick={() => setClosing(session)}>
                  {L("ADM_PPL_CLOSE")}
                </button>
              ),
            },
          ]}
          mobile={(session) => ({
            title: deviceName(L, session),
            line: [session.ip, whenIn(L, locale, session.lastActivityAt, zone)].filter(Boolean).join(" · "),
            end: (
              <button type="button" className="tm-btn secondary" onClick={() => setClosing(session)}>
                {L("ADM_PPL_CLOSE")}
              </button>
            ),
          })}
        />
      )}
      <CloseSessionWindow
        orgId={orgId}
        userId={userId}
        name={name}
        session={closing}
        onClose={() => setClosing(null)}
        onDone={onChanged}
      />
    </>
  );
}

/** `cerrar-sesion` · «¿Cerrar esta sesión?» con quién y en qué dispositivo; si falla, queda abierta con el motivo. */
export function CloseSessionWindow({
  orgId,
  userId,
  name,
  session,
  onClose,
  onDone,
}: {
  orgId: number;
  userId: number;
  name: string;
  session: OpenSession | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  return (
    <ConfirmWindow
      open={!!session}
      title={L("ADM_PPL_CLOSE_ONE_TITLE")}
      line={formatText(L("ADM_PPL_CLOSE_ONE_LINE"), name, session ? deviceName(L, session) : "")}
      action={L("ADM_PPL_CLOSE_ONE")}
      onClose={onClose}
      onConfirm={async () => {
        const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
          `/api/admin/organizations/${orgId}/members/${userId}/sessions/${session!.sessionId}/revoke`,
          {},
        );
        if (reply.status !== 200 || !reply.body?.saved) {
          toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
          return false;
        }
        onDone();
        toast.show(L("ADM_PPL_SESSION_CLOSED"));
        return true;
      }}
    />
  );
}

type Attempt = {
  attemptId: number;
  occurredAt: string;
  success: boolean;
  failureReason: string | null;
  used: string;
  ip: string | null;
};

/** Intentos de ingreso de una persona, paginados en Identity (Fecha · Resultado · Usó · IP). */
export function AttemptsList({
  orgId,
  userId,
  zone,
  version = 0,
}: {
  orgId: number;
  userId: number;
  zone: string;
  /** Sube cuando algo cambió (p. ej., un desbloqueo) para volver a leer. */
  version?: number;
}) {
  const { L, locale } = useTexts();
  const attempts = useServerList<Attempt>(
    `/api/admin/organizations/${orgId}/members/${userId}/sign-in-attempts${version ? `?v=${version}` : ""}`,
  );
  if (attempts.state === "loading") return <RowsSkeleton rows={3} table />;
  if (attempts.state !== "ready")
    return (
      <Notice
        tone="error"
        action={
          <button type="button" className="tm-btn secondary" onClick={attempts.reload}>
            {L("ADM_RETRY")}
          </button>
        }
      >
        {L("ADM_UNAVAILABLE_LINE")}
      </Notice>
    );
  if (attempts.items.length === 0)
    return (
      <div className="tm-empty" role="status">
        <strong>{L("ADM_PPL_NO_ATTEMPTS")}</strong>
      </div>
    );
  return (
    <>
      <SimpleTable
        label={L("ADM_PPL_ATTEMPTS")}
        rows={attempts.items}
        rowKey={(item) => item.attemptId}
        columns={[
          { key: "date", title: L("ADM_PPL_COL_DATE"), render: (item) => whenIn(L, locale, item.occurredAt, zone) },
          {
            key: "result",
            title: L("ADM_PPL_COL_RESULT"),
            render: (item) => {
              const view = attemptText(L, item.success, item.failureReason);
              return view.failed ? <span className="tm-tag npa-tag-error">{view.text}</span> : view.text;
            },
          },
          { key: "used", title: L("ADM_PPL_COL_USED"), render: (item) => item.used },
          { key: "ip", title: L("ADM_PPL_COL_IP"), render: (item) => item.ip ?? "—" },
        ]}
        mobile={(item) => {
          const view = attemptText(L, item.success, item.failureReason);
          return {
            title: whenIn(L, locale, item.occurredAt, zone),
            tag: view.failed ? <span className="tm-tag npa-tag-error">{view.text}</span> : undefined,
            line: [view.failed ? null : view.text, item.used, item.ip].filter(Boolean).join(" · "),
          };
        }}
      />
      <ListFooter list={attempts} />
    </>
  );
}
